/**
 * /admin/users/:id — full detail page with inline mutations.
 *
 * Three cards: Identity (status toggle), Roles (assign + revoke), Grants
 * (create + revoke). Every mutation prompts for an audit reason via the
 * `promptForReason` modal; success / failure surfaces as a toast
 * notification rather than mutating page state silently.
 */

import { useCallback, useEffect, useState } from "react";
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Code,
  Container,
  Divider,
  Group,
  Loader,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { toastApiError, toastSuccess, toastWarning } from "@/shared/ui/toast";
import {
  IconArrowLeft,
  IconDownload,
  IconLogout,
  IconPlus,
  IconTrash,
  IconUserCheck,
  IconUserX,
} from "@tabler/icons-react";
import { useNavigate, useParams } from "react-router-dom";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { ApiError } from "@/shared/api/apiFetch";
import { promptForReason } from "@/shared/ui/promptForReason";
import {
  assignRole,
  deactivateUser,
  exportUser,
  getUser,
  offboardUser,
  reactivateUser,
  revokeRole,
  type UserOut,
} from "@/shared/api/users";
import {
  createGrant,
  listGrants,
  revokeGrant,
  type GrantOut,
} from "@/shared/api/grants";

const ASSIGNABLE_ROLES = ["user", "developer", "administrator"];
const GRANT_RESOURCE_TYPES = ["tenant", "audit_export"];

export function UserDetail() {
  const { userId = "" } = useParams<{ userId: string }>();
  const getToken = useAccessToken();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserOut | null>(null);
  const [grants, setGrants] = useState<GrantOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const [u, g] = await Promise.all([
        getUser(token, userId),
        listGrants(token, { user_id: userId }),
      ]);
      setUser(u);
      setGrants(g.items);
    } catch (err) {
      if (err instanceof ApiError && err.code === "user_not_found") {
        navigate("/admin/users", { replace: true });
        return;
      }
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [getToken, navigate, userId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  if (loading && !user) {
    return (
      <Group justify="center" p="xl">
        <Loader />
      </Group>
    );
  }

  if (error && !user) {
    return (
      <Container size="md" py="lg">
        <Alert color="red" variant="light" title="Could not load user">
          {error}
        </Alert>
      </Container>
    );
  }

  if (!user) return null;

  return (
    <Stack gap="md">
      <Group>
        <Button
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate("/admin/users")}
        >
          Back to users
        </Button>
      </Group>

      <Group justify="space-between">
        <Title order={2}>{user.display_name || "(hidden)"}</Title>
      </Group>

      {error && (
        <Alert color="red" variant="light">
          {error}
        </Alert>
      )}

      <IdentityCard user={user} onChange={reload} />
      <RolesCard user={user} onChange={reload} />
      <GrantsCard userId={user.user_id} grants={grants} onChange={reload} />
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Identity card — status toggle.
// ---------------------------------------------------------------------------

function IdentityCard({ user, onChange }: { user: UserOut; onChange: () => void }) {
  const getToken = useAccessToken();
  const isDeactivated = user.status === "deactivated";
  const pendingErasure = user.pending_erasure_at_utc;
  const [exporting, setExporting] = useState(false);
  const [offboarding, setOffboarding] = useState(false);

  const onToggle = async () => {
    const verb = isDeactivated ? "Reactivate" : "Deactivate";
    const reason = await promptForReason(`${verb} ${user.user_id}`, "Audit reason");
    if (!reason) return;
    try {
      const token = await getToken();
      if (isDeactivated) {
        await reactivateUser(token, user.user_id, reason);
        toastSuccess("Reactivated", "User can sign in again.");
      } else {
        await deactivateUser(token, user.user_id, reason);
        toastWarning("Deactivated", "User can no longer sign in.");
      }
      onChange();
    } catch (err) {
      toastApiError("Action failed", err);
    }
  };

  // Permanent offboarding. Combined "deactivate + schedule erasure"
  // workflow — the backend stamps `pending_erasure_at_utc` and the Tier
  // 3 KEK-destruction runner will later crypto-shred the subject's PII
  // (ADR-0002). Reversible until the runner fires; reactivating before
  // then clears the schedule and the audit row notes the cancellation.
  const onOffboard = async () => {
    const reason = await promptForReason(
      `Offboard ${user.user_id}`,
      "Audit reason",
      {
        warning:
          "This deactivates the user AND queues their PII for crypto-shredding (ADR-0002). " +
          "Reversible by reactivating BEFORE the erasure runner fires; afterwards, " +
          "email and display_name will no longer be readable.",
        confirmLabel: "Offboard",
      },
    );
    if (!reason) return;
    setOffboarding(true);
    try {
      const token = await getToken();
      await offboardUser(token, user.user_id, reason);
      notifications.show({
        title: "Offboarded",
        message: "User deactivated and queued for erasure.",
        color: "red",
      });
      onChange();
    } catch (err) {
      toastApiError("Offboard failed", err);
    } finally {
      setOffboarding(false);
    }
  };

  // GDPR DSAR download (CLAUDE.md §9.4). Fetches the per-subject payload
  // from /api/v1/users/{id}/export and offers it as a JSON download. The
  // backend audits the export — the UI is just a delivery channel.
  const onExport = async () => {
    setExporting(true);
    try {
      const token = await getToken();
      const payload = await exportUser(token, user.user_id);
      const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `daquma-user-export-${user.user_id}.json`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      notifications.show({
        title: "Export downloaded",
        message: "JSON payload saved. The DSAR has been audited.",
        color: "blue",
      });
    } catch (err) {
      toastApiError("Export failed", err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card withBorder radius="md">
      <Group justify="space-between" mb="sm" wrap="wrap">
        <Group gap="sm" align="center">
          <Title order={4}>Identity</Title>
          {pendingErasure && (
            <Tooltip
              label={`Crypto-shredding scheduled at ${pendingErasure}. Reactivating clears the schedule.`}
            >
              <Badge color="red" variant="filled">
                Erasure scheduled
              </Badge>
            </Tooltip>
          )}
        </Group>
        <Group gap="xs">
          <Tooltip label="Download a GDPR-style data export for this subject (audited).">
            <Button
              variant="subtle"
              leftSection={<IconDownload size={16} />}
              onClick={() => void onExport()}
              loading={exporting}
              aria-label="Download data export"
            >
              Download export
            </Button>
          </Tooltip>
          {!isDeactivated && (
            <Tooltip label="Permanent departure: deactivates AND queues PII erasure.">
              <Button
                color="red"
                variant="subtle"
                leftSection={<IconLogout size={16} />}
                onClick={() => void onOffboard()}
                loading={offboarding}
                aria-label="Offboard user"
              >
                Offboard
              </Button>
            </Tooltip>
          )}
          <Button
            color={isDeactivated ? "green" : "red"}
            variant="light"
            leftSection={
              isDeactivated ? <IconUserCheck size={16} /> : <IconUserX size={16} />
            }
            onClick={() => void onToggle()}
          >
            {isDeactivated ? "Reactivate" : "Deactivate"}
          </Button>
        </Group>
      </Group>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
        <Field label="Email" value={user.email || "—"} />
        <Field label="Tenant" value={user.tenant_id} mono />
        <Field label="Status">
          <Badge color={statusColor(user.status)} variant="light">
            {user.status}
          </Badge>
        </Field>
        <Field label="Last login (UTC)" value={user.last_login_at_utc ?? "—"} />
        <Field label="Local user ID" value={user.user_id} mono />
        <Field label="Entra object ID" value={user.entra_object_id} mono />
      </SimpleGrid>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Roles card.
// ---------------------------------------------------------------------------

function RolesCard({ user, onChange }: { user: UserOut; onChange: () => void }) {
  const getToken = useAccessToken();
  const assignable = ASSIGNABLE_ROLES.filter((r) => !user.roles.includes(r));
  const [pending, setPending] = useState<string | null>(assignable[0] ?? null);

  const onAssign = async () => {
    if (!pending) return;
    const reason = await promptForReason(
      `Assign '${pending}' to ${user.user_id}`,
      "Audit reason",
    );
    if (!reason) return;
    try {
      const token = await getToken();
      await assignRole(token, user.user_id, pending, reason);
      toastSuccess("Role assigned", pending);
      onChange();
    } catch (err) {
      toastApiError("Action failed", err);
    }
  };

  const onRevoke = async (role: string) => {
    const reason = await promptForReason(
      `Revoke '${role}' from ${user.user_id}`,
      "Audit reason",
    );
    if (!reason) return;
    try {
      const token = await getToken();
      await revokeRole(token, user.user_id, role, reason);
      toastWarning("Role revoked", role);
      onChange();
    } catch (err) {
      toastApiError("Action failed", err);
    }
  };

  return (
    <Card withBorder radius="md">
      <Title order={4} mb="sm">
        Roles
      </Title>
      {user.roles.length === 0 ? (
        <Text c="dimmed" size="sm" mb="sm">
          (none)
        </Text>
      ) : (
        <Group gap="xs" mb="md">
          {user.roles.map((r) => (
            <Badge
              key={r}
              color="brand"
              variant="light"
              size="lg"
              rightSection={
                <ActionIcon
                  size="xs"
                  variant="transparent"
                  onClick={() => void onRevoke(r)}
                  aria-label={`Revoke ${r}`}
                >
                  <IconTrash size={12} />
                </ActionIcon>
              }
            >
              {r}
            </Badge>
          ))}
        </Group>
      )}

      {assignable.length > 0 && (
        <>
          <Divider my="sm" />
          <Group align="end" gap="xs">
            <Select
              label="Add role"
              data={assignable}
              value={pending}
              onChange={setPending}
              w={200}
            />
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => void onAssign()}
              disabled={!pending}
            >
              Assign
            </Button>
          </Group>
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Grants card.
// ---------------------------------------------------------------------------

function GrantsCard({
  userId,
  grants,
  onChange,
}: {
  userId: string;
  grants: GrantOut[];
  onChange: () => void;
}) {
  const getToken = useAccessToken();
  const [resourceType, setResourceType] = useState<string | null>(
    GRANT_RESOURCE_TYPES[0] ?? null,
  );
  const [resourceId, setResourceId] = useState("");
  const [permission, setPermission] = useState("");

  const onCreate = async () => {
    if (!permission || !resourceType) return;
    const reason = await promptForReason(
      `Grant ${resourceType}.${permission} on ${resourceId || "(any)"}`,
      "Audit reason",
    );
    if (!reason) return;
    try {
      const token = await getToken();
      await createGrant(token, {
        user_id: userId,
        resource_type: resourceType,
        resource_id: resourceId || null,
        permission,
        reason,
      });
      setResourceId("");
      setPermission("");
      toastSuccess("Grant created", `${resourceType}.${permission}`);
      onChange();
    } catch (err) {
      toastApiError("Action failed", err);
    }
  };

  const onRevoke = async (grantId: string) => {
    const reason = await promptForReason("Revoke this grant", "Audit reason");
    if (!reason) return;
    try {
      const token = await getToken();
      await revokeGrant(token, grantId, reason);
      toastWarning("Grant revoked", "Removed from active grants.");
      onChange();
    } catch (err) {
      toastApiError("Action failed", err);
    }
  };

  return (
    <Card withBorder radius="md">
      <Title order={4} mb="sm">
        Grants
      </Title>
      {grants.length === 0 ? (
        <Text c="dimmed" size="sm" mb="md">
          (none)
        </Text>
      ) : (
        <Table withRowBorders={false} verticalSpacing="xs" mb="md">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Type</Table.Th>
              <Table.Th>Resource</Table.Th>
              <Table.Th>Permission</Table.Th>
              <Table.Th style={{ width: 50 }}></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {grants.map((g) => (
              <Table.Tr key={g.grant_id}>
                <Table.Td>{g.resource_type}</Table.Td>
                <Table.Td>
                  <Code>{g.resource_id ?? "(any)"}</Code>
                </Table.Td>
                <Table.Td>{g.permission}</Table.Td>
                <Table.Td>
                  <Tooltip label="Revoke">
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => void onRevoke(g.grant_id)}
                      aria-label="Revoke grant"
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <Divider my="sm" />
      <Stack gap="xs">
        <Text size="sm" fw={500}>
          New grant
        </Text>
        <Group align="end" gap="xs">
          <Select
            label="Resource type"
            data={GRANT_RESOURCE_TYPES}
            value={resourceType}
            onChange={setResourceType}
            w={170}
          />
          <TextInput
            label="Resource ID"
            placeholder="empty = any"
            value={resourceId}
            onChange={(e) => setResourceId(e.currentTarget.value)}
            w={200}
          />
          <TextInput
            label="Permission"
            placeholder="e.g. verify"
            value={permission}
            onChange={(e) => setPermission(e.currentTarget.value)}
            required
            w={170}
          />
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => void onCreate()}
            disabled={!permission}
          >
            Create
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

function statusColor(status: string): string {
  switch (status) {
    case "active":
      return "green";
    case "pending":
      return "yellow";
    case "deactivated":
      return "red";
    default:
      return "gray";
  }
}

interface FieldProps {
  label: string;
  value?: string;
  mono?: boolean;
  children?: React.ReactNode;
}

function Field({ label, value, mono, children }: FieldProps) {
  return (
    <div>
      <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
        {label}
      </Text>
      {children ? (
        <div style={{ marginTop: 2 }}>{children}</div>
      ) : mono ? (
        <Anchor component="span" ff="monospace" size="sm">
          {value}
        </Anchor>
      ) : (
        <Text size="sm">{value}</Text>
      )}
    </div>
  );
}
